"use client";

import { useRef, useEffect, useState } from "react";
import { CldImage } from "next-cloudinary";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { SplitText } from "gsap/SplitText";
import { GSDevTools } from "gsap/GSDevTools";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

import Cable from "@components/history/cable";
import Spark from "@components/history/spark";
import HistoryTimeline from "@components/history/history-timeline";
import TimelineDots from "@components/history/timeline-dots";
import Footer from "@/components/global/footer";
import ScrollPrompt from "@/components/global/scroll-prompt";
import { CloudinaryImage, HomeAudio, HistoricalEventData } from "@/lib/hygraph";

interface HistoryPageClientProps {
  rhImage: CloudinaryImage;
  lhImage: CloudinaryImage;
  fImage: CloudinaryImage;
  tapAudio: HomeAudio;
  historicalEvents: HistoricalEventData[] | null;
  extinctLogoUrl?: string;
}

gsap.registerPlugin(
  useGSAP,
  ScrollTrigger,
  SplitText,
  MotionPathPlugin,
  ScrollSmoother,
  GSDevTools,
  ScrollToPlugin,
);

const HistoryPageClient = ({
  rhImage,
  lhImage,
  fImage,
  tapAudio,
  historicalEvents,
  extinctLogoUrl,
}: HistoryPageClientProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftHandRef = useRef<HTMLDivElement>(null);
  const rightHandRef = useRef<HTMLDivElement>(null);
  const cableContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollPrompt, setShowScrollPrompt] = useState(false);

  // 1. Initial Setup & Hand Animations
  useEffect(() => {
    gsap.set(document.body, { overflow: "auto" });

    let audioContext: AudioContext | null = null;
    let audioBuffer: AudioBuffer | null = null;
    let sourceNode: AudioBufferSourceNode | null = null;

    // Pre-decode audio using Web Audio API for instant playback
    const loadAudio = async () => {
      if (!tapAudio?.url) return;

      try {
        const AudioContextClass =
          window.AudioContext ||
          (
            window as typeof window & {
              webkitAudioContext: typeof AudioContext;
            }
          ).webkitAudioContext;
        audioContext = new AudioContextClass();
        // Ensure HTTPS to prevent mixed content errors
        const audioUrl =
          tapAudio.secure_url || tapAudio.url.replace(/^http:/, "https:");
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      } catch (e) {
        console.warn("Audio loading failed:", e);
      }
    };

    loadAudio();

    if (leftHandRef.current && rightHandRef.current) {
      gsap.set(leftHandRef.current, {
        x: "-100%",
        visibility: "hidden",
        willChange: "transform",
      });
      gsap.set(rightHandRef.current, {
        x: 100,
        opacity: 0,
        visibility: "hidden",
        willChange: "transform, opacity",
      });
      // Position cable container off-screen initially on mobile (slides in with hand)
      if (cableContainerRef.current && window.innerWidth < 768) {
        gsap.set(cableContainerRef.current, {
          x: "30%",
          visibility: "visible",
        });
      }

      const introTl = gsap.timeline({
        delay: 0.5,
        onComplete: () => {
          gsap.set([leftHandRef.current, rightHandRef.current], {
            willChange: "auto",
          });
          if (cableContainerRef.current && window.innerWidth < 768) {
            gsap.set(cableContainerRef.current, { willChange: "auto" });
          }
          setShowScrollPrompt(true);
        },
      });

      introTl.to(
        rightHandRef.current,
        {
          x: 0,
          opacity: 1,
          visibility: "visible",
          duration: 1.2,
          ease: "power2.out",
        },
        0,
      );

      // Animate cable container with the hand on mobile
      if (cableContainerRef.current && window.innerWidth < 768) {
        introTl.to(
          cableContainerRef.current,
          {
            x: 0,
            duration: 1.2,
            ease: "power2.out",
          },
          0, // Same timing as right hand
        );
      }

      introTl.to(
        leftHandRef.current,
        {
          x: "-5%",
          visibility: "visible",
          duration: 1.5,
          ease: "power2.out",
        },
        0.3,
      );

      // Tap down and up 3 times, ending in up position
      introTl.to(".tappy", {
        rotation: 10,
        repeat: 5,
        yoyo: true,
        transformOrigin: "20% 90%",
        duration: 0.1,
        ease: "power1.inOut",
        onStart: () => {
          if (audioContext && audioBuffer) {
            try {
              sourceNode = audioContext.createBufferSource();
              sourceNode.buffer = audioBuffer;
              sourceNode.connect(audioContext.destination);
              sourceNode.start(0);
            } catch (e) {
              console.warn("Audio play failed:", e);
            }
          }
        },
      });
    }

    return () => {
      sourceNode?.stop();
      audioContext?.close();
    };
  }, [tapAudio]);

  // 2. Main Scroll & Path Logic
  useGSAP(
    () => {
      if (!historicalEvents) return;

      const cablePath = document.querySelector("#cable") as SVGPathElement;
      if (!cablePath) return;

      // --- INITIALIZATION ---
      historicalEvents.forEach((event, index) => {
        const dot = document.querySelector(`[data-dot-index="${index}"]`);
        const card = document.querySelector(`[data-event-id="${event.id}"]`);

        if (dot) {
          gsap.set(dot, {
            autoAlpha: 0,
            zIndex: 10,
            width: "16px",
            height: "16px",
            border: "none",
            backgroundColor: "transparent",
            boxShadow: "none",
          });

          const innerPulse = dot.querySelector(".dot-pulse");
          if (innerPulse) {
            gsap.set(innerPulse, {
              display: "block",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(168, 85, 247, 1) 0%, rgba(168, 85, 247, 0.8) 40%, rgba(168, 85, 247, 0) 70%)",
              scale: 0,
              opacity: 1,
              boxShadow: "0 0 0px rgba(168, 85, 247, 0)",
            });
          }
        }

        if (card) {
          // Set initial state for card container
          gsap.set(card, {
            autoAlpha: 0,
            scale: 0.95,
            zIndex: 10,
            willChange: "opacity, transform",
          });
        }
      });

      // --- POSITIONING LOGIC ---
      // Define safe zones that avoid loop sections
      const isMobile = window.innerWidth < 768;

      // Desktop: two safe zones (before second loop, after second loop)
      // Mobile: two safe zones (before loop, after loop)
      // Extended to use full cable length (last card at the very end)
      const safeZones = isMobile
        ? [
            { start: 0.04, end: 0.07 }, // Before mobile loop
            { start: 0.16, end: 0.99 }, // After mobile loop, to end
          ]
        : [
            { start: 0.2, end: 0.55 }, // After first loop, before second
            { start: 0.75, end: 0.99 }, // After second loop, to end
          ];

      // Calculate total safe zone length and distribute cards evenly
      const totalSafeLength = safeZones.reduce(
        (sum, zone) => sum + (zone.end - zone.start),
        0,
      );

      const getPathProgressForCard = (
        cardIndex: number,
        totalCards: number,
      ) => {
        // Space cards so first card has padding from start, last card is at the end
        // With N cards: first at 1/(N+1), last at N/(N+1) of the way through
        // Adjust so last card reaches the end: use N divisions, not N+1
        const spacing = totalCards > 1 ? totalSafeLength / (totalCards - 1) : 0;
        let targetPos = totalCards > 1 ? spacing * cardIndex : totalSafeLength / 2;

        // Map position to actual path progress within safe zones
        for (const zone of safeZones) {
          const zoneLength = zone.end - zone.start;
          if (targetPos <= zoneLength) {
            return zone.start + targetPos;
          }
          targetPos -= zoneLength;
        }
        // Fallback to end of last zone
        return safeZones[safeZones.length - 1].end;
      };

      const updatePositions = () => {
        const pathData = cablePath.getAttribute("d");
        if (!pathData || pathData.length < 5) {
          requestAnimationFrame(updatePositions);
          return;
        }

        historicalEvents.forEach((event, index) => {
          const pathProgress = getPathProgressForCard(
            index,
            historicalEvents.length,
          );
          const dot = document.querySelector(`[data-dot-index="${index}"]`);
          const card = document.querySelector(
            `[data-event-id="${event.id}"]`,
          ) as HTMLElement;

          if (dot) {
            gsap.set(dot, {
              motionPath: {
                path: "#cable",
                align: "#cable",
                alignOrigin: [0.5, 0.5],
                start: pathProgress,
                end: pathProgress,
              },
              xPercent: -50,
              yPercent: -50,
              transformOrigin: "center center",
              position: "absolute",
              left: 0,
              top: 0,
            });
          }

          if (card) {
            const viewportW = window.innerWidth;
            const isMobile = viewportW < 768;
            const edgePadding = isMobile ? 12 : 24;

            // Get the actual point position on the cable path
            const pathLength = cablePath.getTotalLength();
            const pointOnPath = cablePath.getPointAtLength(
              pathLength * pathProgress,
            );

            // Get SVG element to convert SVG coords to viewport
            const svgElement = cablePath.ownerSVGElement;
            const svgRect = svgElement?.getBoundingClientRect();

            // Calculate the point's X position in viewport coordinates
            let pointViewportX = pointOnPath.x;
            if (svgRect && svgElement) {
              const viewBox = svgElement.viewBox.baseVal;
              const scaleX = svgRect.width / viewBox.width;
              pointViewportX = svgRect.left + pointOnPath.x * scaleX;
            }

            // Cable point on left = card goes right, and vice versa
            const cablePointOnLeft = pointViewportX < viewportW / 2;
            let cardGoesRight = cablePointOnLeft; // Opposite side of cable
            const cardOffset = isMobile ? 30 : 80;

            // Position card on opposite side of cable point
            const cardWidth = isMobile ? Math.min(280, viewportW - edgePadding * 2) : 420;

            let cardX: number;
            if (cardGoesRight) {
              cardX = pointViewportX + cardOffset;
              // If it overflows right, check if it fits better on the left
              if (cardX + cardWidth > viewportW - edgePadding) {
                const leftX = pointViewportX - cardOffset - cardWidth;
                if (leftX >= edgePadding) {
                  cardX = leftX;
                  cardGoesRight = false;
                }
              }
            } else {
              cardX = pointViewportX - cardOffset - cardWidth;
              // If it overflows left, check if it fits better on the right
              if (cardX < edgePadding) {
                const rightX = pointViewportX + cardOffset;
                if (rightX + cardWidth <= viewportW - edgePadding) {
                  cardX = rightX;
                  cardGoesRight = true;
                }
              }
            }

            // Final safety clamp to viewport bounds
            cardX = Math.max(edgePadding, cardX);
            cardX = Math.min(viewportW - cardWidth - edgePadding, cardX);

            card.dataset.side = cardGoesRight ? "right" : "left";

            // Get Y position from the path point
            let pointViewportY = pointOnPath.y;
            if (svgRect && svgElement) {
              const viewBox = svgElement.viewBox.baseVal;
              const scaleY = svgRect.height / viewBox.height;
              pointViewportY = svgRect.top + pointOnPath.y * scaleY;
            }

            // Position card absolutely relative to full-width micAndCable container
            const fullWidthContainer = document.querySelector(".micAndCable");
            const containerRect = fullWidthContainer?.getBoundingClientRect();
            const relativeX = cardX - (containerRect?.left || 0);
            const relativeY = pointViewportY - (containerRect?.top || 0);

            gsap.set(card, {
              position: "absolute",
              left: relativeX,
              top: relativeY,
              xPercent: 0,
              yPercent: -50,
              x: 0,
              y: 0,
            });
          }
        });
      };

      updatePositions();

      // Force recalculation after layout stabilizes (fixes mobile portrait initial load)
      // Using multiple frames ensures CSS layout, paint, and composite are complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updatePositions();
          ScrollTrigger.refresh();
        });
      });

      // Also recalculate on window load (fonts, images may affect layout)
      const handleLoad = () => {
        updatePositions();
        ScrollTrigger.refresh();
      };
      if (document.readyState === 'complete') {
        handleLoad();
      } else {
        window.addEventListener('load', handleLoad);
      }

      ScrollTrigger.addEventListener("refresh", updatePositions);

      // ResizeObserver for reliable layout change detection (orientation changes, etc.)
      const resizeObserver = new ResizeObserver(() => {
        updatePositions();
      });
      if (cableContainerRef.current) {
        resizeObserver.observe(cableContainerRef.current);
      }

      // --- OFF-SCREEN OPTIMIZATION ---
      // Hide the heavy hand images when scrolled well past them to free up GPU resources
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "1200px top", // When scrolled down 1200px (hands definitely gone)
        onLeave: () => {
          gsap.set([leftHandRef.current, rightHandRef.current], {
            autoAlpha: 0,
            overwrite: true,
          });
        },
        onEnterBack: () => {
          gsap.set([leftHandRef.current, rightHandRef.current], {
            autoAlpha: 1,
            overwrite: true,
          });
        },
      });

      // --- PULSE ANIMATION (Child Element) ---
      const runPulseAnimation = (dotElement: Element) => {
        const target = dotElement.querySelector(".dot-pulse");
        if (!target) return;

        const isMobile = window.innerWidth < 768;
        // Mobile pulses are half size since cable is thinner
        const s = isMobile ? 0.5 : 1;

        const tl = gsap.timeline();

        // 1. INITIAL WHITE FLASH - quick wide burst with more white
        tl.fromTo(
          target,
          {
            scale: 0,
            opacity: 1,
            background:
              "radial-gradient(circle, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.8) 25%, rgba(168, 85, 247, 0.7) 50%, rgba(168, 85, 247, 0) 75%)",
            boxShadow: "0 0 15px rgba(255, 255, 255, 0.7), 0 0 30px rgba(168, 85, 247, 0.5)",
          },
          {
            scale: 10 * s,
            opacity: 1,
            duration: 0.12,
            ease: "power2.out",
            overwrite: true,
          },
        );

        // 2. TRANSITION TO PURPLE - white fades quickly
        tl.to(target, {
          scale: 5 * s,
          background:
            "radial-gradient(circle, rgba(168, 85, 247, 1) 0%, rgba(168, 85, 247, 0.8) 40%, rgba(168, 85, 247, 0) 70%)",
          boxShadow: "0 0 12px rgba(168, 85, 247, 0.7), 0 0 25px rgba(168, 85, 247, 0.4)",
          duration: 0.2,
          ease: "power2.inOut",
        });

        // 3. SETTLE to consistent resting state
        tl.to(target, {
          scale: 2.5 * s,
          opacity: 0.8,
          background:
            "radial-gradient(circle, rgba(168, 85, 247, 1) 0%, rgba(168, 85, 247, 0.7) 40%, rgba(168, 85, 247, 0) 70%)",
          boxShadow: "0 0 10px rgba(168, 85, 247, 0.5), 0 0 20px rgba(168, 85, 247, 0.3)",
          duration: 0.4,
          ease: "power2.inOut",
        });

        // 4. HEARTBEAT LOOP - very subtle pulse to keep dots consistent
        tl.to(target, {
          scale: 2.8 * s,
          opacity: 0.75,
          duration: 2,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
        });
      };

      // Store card animation data for onUpdate callback
      type CardAnimData = {
        eventId: string;
        triggerPos: number;
        timeline: gsap.core.Timeline;
        isShowing: boolean;
      };
      const cardAnimations: CardAnimData[] = [];

      const initTimeline = () => {
        const pathData = cablePath.getAttribute("d");
        if (!pathData || pathData.length < 5) {
          requestAnimationFrame(initTimeline);
          return;
        }

        const isMobile = window.innerWidth < 768;

        // Convert path progress to timeline progress
        const pathToTimeline = (pathPos: number): number => {
          if (isMobile) {
            // Mobile Logic (Simplified)
            // Segment 1: 0-0.16 path -> 0-0.08 timeline (includes loop)
            if (pathPos <= 0.16) return (pathPos / 0.16) * 0.08;
            // Segment 2: 0.16-1.0 path -> 0.08-1.0 timeline (Steady)
            return 0.08 + ((pathPos - 0.16) / 0.84) * 0.92;
          } else {
            // Desktop Logic (4 Segments)
            // Segment 1: 0-0.12 path -> 0-0.08 timeline
            if (pathPos <= 0.12) return (pathPos / 0.12) * 0.08;
            // Segment 2: 0.12-0.55 path -> 0.08-0.58 timeline (Speed 0.50)
            if (pathPos <= 0.55) return 0.08 + ((pathPos - 0.12) / 0.43) * 0.5;
            // Segment 3: 0.55-0.7 path -> 0.58-0.63 timeline
            if (pathPos <= 0.7) return 0.58 + ((pathPos - 0.55) / 0.15) * 0.05;
            // Segment 4: 0.7-1.0 path -> 0.63-1.0 timeline (Balanced)
            return 0.63 + ((pathPos - 0.7) / 0.3) * 0.37;
          }
        };

        // Pre-calculate card trigger positions and create animation timelines
        historicalEvents.forEach((event, index) => {
          const pathProgress = getPathProgressForCard(
            index,
            historicalEvents.length,
          );
          const timelinePos = pathToTimeline(pathProgress);
          const card = document.querySelector(`[data-event-id="${event.id}"]`);

          if (card) {
            // Create card animation timeline
            const cardTl = gsap.timeline({ paused: true });

            // Animate card container in (scale 0.95→1, fade in)
            cardTl.to(card, {
              autoAlpha: 1,
              scale: 1,
              duration: 0.5,
              ease: "power2.out",
            });

            cardAnimations.push({
              eventId: event.id,
              triggerPos: timelinePos,
              timeline: cardTl,
              isShowing: false,
            });
          }
        });

        // Initialize spark opacity
        gsap.set(".spark", { opacity: 0 });

        // Spark Opacity: Fade In (first 500px)
        gsap.fromTo(
          ".spark",
          { opacity: 0 },
          {
            opacity: 1,
            ease: "none",
            scrollTrigger: {
              trigger: ".cable-container",
              start: "top 70%",
              end: "+=500",
              scrub: true,
            },
          },
        );

        // Spark Opacity: Fade Out (last 1000px of scroll range)
        gsap.fromTo(
          ".spark",
          { opacity: 1 },
          {
            opacity: 0,
            ease: "none",
            scrollTrigger: {
              trigger: ".cable-container",
              start: isMobile ? "bottom bottom" : "bottom-=1000 bottom",
              end: isMobile ? "bottom+=250 bottom" : "bottom bottom",
              scrub: true,
            },
          },
        );

        // JITTER FIX:
        // scrub: true (instant) prevents catch-up animation that causes jitter
        const sparkTl = gsap.timeline({
          scrollTrigger: {
            trigger: ".cable-container",
            // Mobile: start at 30% for later spark movement, extended end for slower travel
            // Desktop: start at 70% for earlier spark movement
            start: isMobile ? "top 30%" : "top 70%",
            end: isMobile ? "bottom+=250 bottom" : "bottom bottom",
            scrub: true, // Use true for instant scrubbing (no smoothing delay)
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const progress = self.progress;

              // Trigger card animations based on spark progress
              cardAnimations.forEach((cardAnim) => {
                const shouldShow = progress >= cardAnim.triggerPos;

                if (shouldShow && !cardAnim.isShowing) {
                  // Spark passed this point - animate in
                  cardAnim.timeline.play();
                  cardAnim.isShowing = true;
                } else if (!shouldShow && cardAnim.isShowing) {
                  // Spark moved back - animate out
                  cardAnim.timeline.reverse();
                  cardAnim.isShowing = false;
                }
              });
            },
          },
        });

        if (isMobile) {
          // MOBILE: Single continuous tween with custom ease (same approach as desktop)
          // This eliminates segment boundary timing issues
          const mobileSparkTween = gsap.to(".spark", {
            motionPath: {
              path: "#cable",
              align: "#cable",
              alignOrigin: [0.5, 0.5],
              autoRotate: true,
              start: 0,
              end: 1,
              resolution: 200,
            },
            ease: "none",
            paused: true,
            force3D: true,
            willChange: "transform",
          });

          // Custom ease: scroll progress (0-1) to path progress (0-1)
          // Loop is at path 0.07-0.16, so speed through it
          // Must be exact inverse of pathToTimeline for perfect sync
          const mobileSparkEase = (t: number) => {
            // Scroll 0-0.08 → Path 0-0.16 (fast through loop section)
            if (t <= 0.08) return (t / 0.08) * 0.16;
            // Scroll 0.08-1.0 → Path 0.16-1.0 (steady)
            return 0.16 + ((t - 0.08) / 0.92) * 0.84;
          };

          sparkTl.to(
            { p: 0 },
            {
              p: 1,
              duration: 1,
              ease: "none",
              onUpdate: function () {
                const scrollProgress = this.progress();
                const pathProgress = mobileSparkEase(scrollProgress);
                mobileSparkTween.progress(pathProgress);
              },
            },
            0,
          );
        } else {
          // DESKTOP: Single continuous tween with custom ease
          // This eliminates all jumping/glitching at segment boundaries.

          // Create a MASTER tween for the spark path (0 -> 1)
          // This ensures the path calculation is continuous and prevents jumping.
          // We will scrub this tween's progress using the scroll timeline.
          const sparkPathTween = gsap.to(".spark", {
            motionPath: {
              path: "#cable",
              align: "#cable",
              alignOrigin: [0.5, 0.5],
              autoRotate: false, // Disabled to prevent jumping at loop crossings
              start: 0,
              end: 1,
              resolution: 200, // Increased to 200 to prevent jumping on sharp curves
            },
            ease: "none",
            paused: true, // We scrub it manually
          });

          // Custom ease function mapping Scroll Progress (0-1) to Path Progress (0-1)
          // This ensures a single continuous movement with variable speeds.
          const desktopSparkEase = (t: number) => {
            // Segment 1: Loop 1 (Fast)
            // Scroll: 0 -> 0.08 | Path: 0 -> 0.12
            if (t <= 0.08) return (t / 0.08) * 0.12;

            // Segment 2: Straight (Slow/Steady)
            // Scroll: 0.08 -> 0.58 (Duration 0.50) | Path: 0.12 -> 0.55 (Dist 0.43)
            if (t <= 0.58) return 0.12 + ((t - 0.08) / 0.5) * 0.43;

            // Segment 3: Loop 2 (Fast)
            // Scroll: 0.58 -> 0.63 (Duration 0.05) | Path: 0.55 -> 0.70 (Dist 0.15)
            if (t <= 0.63) return 0.55 + ((t - 0.58) / 0.05) * 0.15;

            // Segment 4: End (Steady)
            // Scroll: 0.63 -> 1.0 (Duration 0.37) | Path: 0.70 -> 1.0 (Dist 0.30)
            return 0.7 + ((t - 0.63) / 0.37) * 0.3;
          };

          // Scrub the master tween
          sparkTl.to(
            { p: 0 }, // Dummy object
            {
              p: 1,
              duration: 1,
              ease: "none",
              onUpdate: function () {
                const scrollProgress = this.progress();
                const pathProgress = desktopSparkEase(scrollProgress);
                sparkPathTween.progress(pathProgress);
              },
            },
            0,
          );
        }

        // Add dot pulse triggers to the timeline
        historicalEvents.forEach((_, index) => {
          const pathProgress = getPathProgressForCard(
            index,
            historicalEvents.length,
          );
          const timelinePos = pathToTimeline(pathProgress);
          const dot = document.querySelector(`[data-dot-index="${index}"]`);

          if (dot) {
            sparkTl.to(
              dot,
              {
                autoAlpha: 1,
                duration: 0.001,
                onStart: () => runPulseAnimation(dot),
              },
              timelinePos,
            );
          }
        });
      };

      initTimeline();

      return () => {
        window.removeEventListener('load', handleLoad);
        resizeObserver.disconnect();
        ScrollTrigger.removeEventListener("refresh", updatePositions);
        ScrollTrigger.getAll().forEach((t) => t.kill());
      };
    },
    { scope: containerRef, dependencies: [historicalEvents] },
  );

  return (
    <div
      ref={containerRef}
      className="home-container relative gradient overflow-x-hidden w-full max-w-[100vw]"
      id="scroll-smoother"
    >
      <div className="micAndCable pt-[20vw] relative w-full">
        {/* Right Hand */}
        <div ref={rightHandRef} className="invisible">
          <CldImage
            className="w-[37vw] ml-auto"
            src={rhImage.public_id}
            width={rhImage.width}
            height={rhImage.height}
            alt="Home Intro 1"
          />
        </div>

        {/* Left Hand */}
        <div className="absolute top-[16vw] left-0 w-full h-[50vh] md:h-[32vh] pointer-events-none z-20 overflow-hidden md:overflow-visible">
          <div
            ref={leftHandRef}
            className="absolute top-0 left-0 w-[136%] h-full invisible"
          >
            <CldImage
              className="absolute w-[50%] top-[10px]"
              src={lhImage.public_id}
              width={lhImage.width}
              height={lhImage.height}
              alt="Left Hand"
            />
            <CldImage
              className="tappy absolute left-[38%] w-[18%] top-[10px]"
              src={fImage.public_id}
              width={fImage.width}
              height={fImage.height}
              alt="Tapping Finger"
            />
          </div>
        </div>

        <div
          ref={cableContainerRef}
          className="cable-container w-[70vw] ml-auto relative right-[11.6vw] top-[-16vw] overflow-visible"
        >
          <Cable />
          <TimelineDots events={historicalEvents} />
          <Spark />
        </div>

        {/* Timeline cards positioned relative to full-width micAndCable container */}
        <HistoryTimeline events={historicalEvents} />
      </div>

      <Footer extinctLogo={extinctLogoUrl} />
      {showScrollPrompt && (
        <ScrollPrompt onHide={() => setShowScrollPrompt(false)} />
      )}
    </div>
  );
};

export default HistoryPageClient;
